import React, { useState, useRef, useEffect } from 'react';
import gamesService from '../../services/games';
import { getChosung } from '../../utils/koreanUtils';
import { resolveApiImageUrl } from '../../utils/logoUtils';
import './SearchBox.css';

/**
 * 루미나 화면 층 신작 — 블랙 `components/common/SearchBox.jsx` 의 로직 그대로, JSX·CSS 만 교체(시안 블록 10 `gp-` 검색 결과 줄).
 * WHY: SPEC §2 — 0.2초 디바운스 검색(게임 = 검색 API·폴백, 게임사 = 목록 필터), 결과 정규화, 바깥 클릭 닫기, 선택 콜백은 원본 그대로.
 *      화면은 그라디언트 테두리 알약 입력 + 아래로 펼쳐지는 결과 목록(시안 .gp-item: 썸네일/캐릭터 · 이름 · 게임사 또는 "N개 게임").
 */
const SearchBox = ({ placeholder, type = 'game', onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // SearchBox 상태 추적

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        // 드롭다운 닫기
        setIsOpen(false);
        // 검색어 초기화
        setSearchTerm('');
        // 검색 결과 초기화
        setResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const performSearch = async (value) => {
    setIsLoading(true);

    try {
      let searchResults = [];

      // API를 통한 실제 검색 실행
      if (type === 'game') {
        const response = await gamesService.searchGames(value, 'game');
        searchResults = response.results || response.games || response.data || [];

        // 검색 결과 형식 정규화
        searchResults = searchResults.map(game => ({
          id: game.id,
          name: game.game_name_ko || game.game_name,
          game_name: game.game_name,
          game_name_ko: game.game_name_ko,
          vendor: game.vendor || game.provider || game.category_name,
          vendor_ko: game.vendor || game.provider || game.category_name_ko,
          provider: game.vendor || game.provider || game.category_name,
          image: game.thumbnail_url,
          thumbnail_url: game.thumbnail_url
        }));
      } else {
        const response = await gamesService.searchProviders(value);
        searchResults = response.providers || [];

        // 검색 결과 형식 정규화
        searchResults = searchResults.map(provider => ({
          id: provider.provider,
          name: provider.name_ko || provider.name || provider.provider,
          provider_name: provider.provider,
          games_count: provider.game_count,
          image: provider.logo_url,
          logo_url: provider.logo_url,
          popularGames: provider.popularGames || []
        }));
      }

      setResults(searchResults);

      // 결과가 있으면 무조건 드롭다운 열기
      if (searchResults.length > 0) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }

    } catch (error) {
      setResults([]);
      setIsOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // 디바운스 처리
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 테스트를 위해 빈 입력에도 샘플 데이터 표시
    if (!value.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      performSearch(value);
    }, 200);
  };

  const handleFocus = () => {
    if (searchTerm && results.length > 0) {
      setIsOpen(true);
    }
  };

  const handleItemClick = (item) => {
    if (onSelect) {
      onSelect(item);
    }
    setSearchTerm('');
    setResults([]);
    setIsOpen(false);
  };

  const getItemDisplayName = (item) => {
    if (type === 'game') {
      return item.game_name_ko || item.game_name || item.name;
    } else {
      return item.name || item.provider_name;
    }
  };

  const getItemImage = (item) => {
    if (type === 'game') {
      return item.image || item.thumbnail_url || null;
    } else {
      return item.image || item.logo_url || null;
    }
  };

  /* [루미나] 이미지 주소 정리 — 유저 API 가 썸네일 없음에 문자열 'null' 을 내려보내는 경우(실측)는 없음으로 보고,
     게임사 로고 상대 경로는 게임사 카드와 같은 규칙(resolveApiImageUrl)으로 푼다 */
  const resolveItemImage = (item) => {
    const raw = getItemImage(item);
    if (!raw || raw === 'null') return null;
    return type === 'provider' ? resolveApiImageUrl(raw) : raw;
  };
  const firstChar = (item) => ((getItemDisplayName(item) || '?').trim().charAt(0) || '?');


  return (
    <div
      ref={searchRef}
      className={`search-box ${type === 'provider' ? 'provider-search' : ''}${isOpen ? ' open' : ''}`}
    >
      <label className="search-field">
        <input
          type="text"
          className="search-input"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleFocus}
          autoComplete="off"
        />
        <button className="search-button" type="button" aria-label="검색" tabIndex={-1}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>
        </button>
      </label>

      {/* 강제로 드롭다운 표시 조건 단순화 */}

      {(results.length > 0) && (
        <div
          className="search-results-dropdown"
        >
          {isLoading ? (
            <div className="search-loading">
              검색 중...
            </div>
          ) : (
            results.map((item, index) => (
              <div
                key={`${item.id || index}-${type}`}
                className="search-result-item gp-item"
                role="button"
                tabIndex={0}
                onClick={() => handleItemClick(item)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleItemClick(item); } }}
              >
                <div className="search-result-image gp-th">
                  <span className="gp-ph" aria-hidden="true">{firstChar(item)}</span>
                  {resolveItemImage(item) ? (
                    <img
                      src={resolveItemImage(item)}
                      alt={getItemDisplayName(item)}
                      className={type === 'provider' ? 'gp-th-chr' : undefined}
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.classList.add('no-image');
                      }}
                    />
                  ) : null}
                </div>
                <div className="gp-txt">
                  <div className="gp-nm">
                    {getItemDisplayName(item)}
                  </div>
                  {type === 'game' && (
                    <div className="gp-sub">
                      {item.vendor_ko || item.vendor || item.provider}
                    </div>
                  )}
                  {type === 'provider' && item.games_count && (
                    <div className="gp-sub">
                      {item.games_count}개 게임
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBox;
